import { Button, Card, Flex, Text } from '@servicetitan/anvil2';
import {
    DataItem,
    formatCurrency,
    formatDateWithLocale,
    isSafeHttpUrl,
    openWebUrl,
} from '@servicetitan/financing-utils-shared';
import { ApplicationDataStore } from '@servicetitan/financing-application-shared';
import { ProviderLogo } from '@servicetitan/financing-provider-assets';
import {
    SetupDataStore,
    useSecondLookIntegration,
    useSecondLookProviderName,
} from '@servicetitan/financing-workflow-shared';
import { DecisionDataStore } from '../stores/DecisionDataStore';
import { DeclinedWarningBox } from './DeclinedWarningBox';
import UnderReviewIllustration from '../../common/illustrations/updating.svg';

/**
 * Landing page for second-look providers that continue the application on their own site. Uses
 * redirectUrl, which is distinct from the decline Explore Options path's declineRedirectUrl.
 */
export const Redirect = () => {
    const { decisionData } = DecisionDataStore.useStore();
    const { applicationData } = ApplicationDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { secondLookProvider } = useSecondLookIntegration();
    const secondLookProviderName = useSecondLookProviderName();

    const redirectUrl = decisionData?.redirectUrl;
    const safeRedirectUrl = isSafeHttpUrl(redirectUrl) ? redirectUrl : null;

    if (!safeRedirectUrl) {
        return null;
    }

    const primaryApplicant = applicationData.applicant.primaryApplicant;
    const applicantName = [primaryApplicant?.firstName, primaryApplicant?.lastName]
        .filter(Boolean)
        .join(' ');

    return (
        <Flex
            direction="column"
            gap="4"
            alignItems="center"
            style={{ maxWidth: '845px', width: '100%', margin: '0 auto' }}
        >
            <DeclinedWarningBox />
            <Card flexDirection="column" gap="6" style={{ width: '100%' }}>
                <Flex direction="column" gap="2" alignItems="center">
                    <img src={UnderReviewIllustration} alt="" />
                    <Text
                        variant="headline"
                        el="h2"
                        size="small"
                        style={{ textAlign: 'center', marginBlockEnd: 0 }}
                    >
                        Your application is under review with {secondLookProviderName}.
                    </Text>
                    <Text variant="body" color="secondary" style={{ textAlign: 'center' }}>
                        {secondLookProviderName} needs a bit more information before they can
                        complete their review.
                    </Text>
                </Flex>
                <Flex direction="column" gap="4">
                    <Flex direction="row" gap="4" style={{ flexWrap: 'wrap' }}>
                        <DataItem
                            label="Provider"
                            text={
                                <Flex gap="1" alignItems="center">
                                    {secondLookProviderName}{' '}
                                    <ProviderLogo
                                        provider={secondLookProvider}
                                        height="24px"
                                        maxWidth="68px"
                                    />
                                </Flex>
                            }
                            style={{ flex: '1 1 200px' }}
                        />
                        <DataItem
                            label="Loan Amount"
                            text={formatCurrency(decisionData?.approvedAmount ?? 0)}
                            style={{ flex: '1 1 200px' }}
                        />
                    </Flex>
                    <Flex direction="row" gap="4" style={{ flexWrap: 'wrap' }}>
                        <DataItem
                            label="Applicant Name"
                            text={applicantName}
                            style={{ flex: '1 1 200px' }}
                        />
                        <DataItem
                            label="Application Date"
                            text={formatDateWithLocale(new Date())}
                            style={{ flex: '1 1 200px' }}
                        />
                    </Flex>
                </Flex>
            </Card>
            <Button
                type="button"
                appearance="primary"
                style={{ width: '100%' }}
                onClick={() => openWebUrl(safeRedirectUrl, setupData?.useInAppBrowser)}
            >
                Continue To {secondLookProviderName}
            </Button>
        </Flex>
    );
};
